<template>
  <div class="product-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.product.ProductList.line6_text') }}</h1>
      <div class="header-actions">
        <button class="add-btn" @click="addProduct">{{ $t('wholesaler.product.ProductList.line8_text') }}</button>
      </div>
    </header>

    <div class="main-content">
      <!-- 左侧分类导航 -->
      <div class="category-sidebar">
        <div class="category-title">{{ $t('wholesaler.product.ProductList.line15_text') }}</div>
        <div 
          class="category-item" 
          :class="{ active: currentCategory === '' }"
          @click="filterByCategory('')"
        >
          {{ $t('wholesaler.ProductList.all') }}
        </div>
        <div 
          v-for="cat in categories" 
          :key="cat.name"
          class="category-item" 
          :class="{ active: currentCategory === cat.name }"
          @click="filterByCategory(cat.name)"
        >
          {{ cat.icon }} {{ $t('wholesaler.ProductList.' + getCategoryKey(cat.name)) }}
        </div>
      </div>

      <!-- 右侧商品列表 -->
      <div class="product-list-wrapper">
        <!-- 批量操作栏 -->
        <div class="batch-action-bar">
          <label class="select-all">
            <input type="checkbox" v-model="selectAll" @change="handleSelectAll">
            {{ $t('wholesaler.ProductList.selectAll') }}
          </label>
          <div class="batch-actions">
            <button class="batch-btn onshelf" @click="batchOnshelf" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line43_text') }}</button>
            <button class="batch-btn offshelf" @click="batchOffshelf" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line44_text') }}</button>
            <button class="batch-btn price" @click="openPriceModal" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line45_text') }}</button>
            <button class="batch-btn wholesale" @click="goToWholesalePrice" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line46_text') }}</button>
            <button class="batch-btn stock" @click="openBatchStockAdjust" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line47_text') }}</button>
            <button class="batch-btn order" @click="goToBatchOrder" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line48_text') }}</button>
            <button class="batch-btn share" @click="batchShareProducts" :disabled="selectedProducts.length === 0">{{ $t('wholesaler.product.ProductList.line49_text') }}</button>
          </div>
        </div>

        <!-- 搜索栏 -->
        <div class="search-bar">
          <input 
            type="text" 
            v-model="searchQuery"
            :placeholder="$t('wholesaler.product.ProductList.line58_placeholder')"
            class="search-input"
            @input="handleSearch"
          />
        </div>

        <!-- 商品列表 -->
        <div class="product-list">
          <div v-for="product in filteredProducts" :key="product.id" class="product-item">
            <!-- 商品名称单独一行，像作文标题 -->
            <div class="product-name-title">
              <span class="status-dot" :class="product.enabled ? 'onshelf' : 'offshelf'"></span>
              {{ product.name }}
              <span class="status-label" :class="product.enabled ? 'onshelf' : 'offshelf'">{{ product.enabled ? $t('wholesaler.ProductList.onShelf') : $t('wholesaler.ProductList.offShelf') }}</span>
            </div>
            
            <!-- 下面左 checkbox + 图片，右信息 -->
            <div class="product-body">
              <div class="product-left">
                <input type="checkbox" class="product-checkbox" v-model="selectedProducts" :value="product.id">
                <div class="product-image" @click.stop="openImagePreview(product)">
                <img 
                  v-if="product.mainImage && !product.imageError" 
                  :src="getImageUrl(product.mainImage)" 
                  :alt="$t('wholesaler.product.ProductList.line82_alt')"
                  class="product-img"
                  @error="product.imageError = true"
                />
                <div v-if="!product.mainImage || product.imageError" class="product-icon">{{ getProductIcon(product) }}</div>
                <!-- 多图指示 -->
                <div v-if="getProductImages(product).length > 1" class="image-count-badge">
                  📷 {{ getProductImages(product).length }}
                </div>
              </div>
              </div>
              
              <div class="product-info">
                <div class="product-meta">{{ product.categoryName }} · {{ product.sku }}</div>
                
                <div class="price-column">
                  <div class="price-item">
                    <span class="price-label">{{ $t('wholesaler.product.ProductList.line99_text') }}</span>
                    <div class="price-value-wrapper" @click="editMarketPrice(product)">
                      <span class="price-value">C$ {{ product.marketPrice || '0.00' }}</span>
                      <span class="edit-icon">✏️</span>
                    </div>
                  </div>
                  <div class="price-item">
                    <span class="price-label">{{ $t('wholesaler.product.ProductList.line106_text') }}</span>
                    <div class="price-value-wrapper" @click="goToWholesalePriceList(product)">
                      <span class="price-action" v-if="!product.minWholesalePrice">{{ $t('wholesaler.product.ProductList.line108_text') }}</span>
                      <span class="price-value wholesale" v-else>C$ {{ product.minWholesalePrice }}</span>
                      <span class="edit-icon">✏️</span>
                    </div>
                  </div>
                </div>
                
                <!-- 库存显示 -->
                <div class="stock-column">
                  <div class="stock-item">
                    <span class="stock-label">{{ $t('wholesaler.product.ProductList.line118_text') }}</span>
                    <div class="stock-value-wrapper" @click="openStockAdjust(product)">
                      <span class="stock-value" :class="{ 'multi-warehouse': product.hasMultiWarehouse }">
                        {{ product.totalStock || 0 }} {{ product.unit || $t('wholesaler.ProductList.kilogram') }}
                      </span>
                      <span class="edit-icon">✏️</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 按钮放到 product-body 外面 -->
            <div class="product-actions-bottom">
              <button class="edit-btn" @click="editProduct(product)">{{ $t('common.edit') }}</button>
              <button class="edit-btn order-btn" @click="goToSingleOrder(product)">{{ $t('wholesaler.product.ProductList.line133_text') }}</button>
              <button class="edit-btn share-btn" @click="shareProduct(product)">{{ $t('wholesaler.product.ProductList.line134_text') }}</button>
              <div 
                class="product-status" 
                :class="product.enabled ? 'onshelf' : 'offshelf'"
                @click="toggleProductStatus(product)"
              >
                {{ product.enabled ? $t('wholesaler.ProductList.onShelf') : $t('wholesaler.ProductList.offShelf') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 库存调整弹窗 -->
    <MultiWarehouseDialog 
      :key="stockAdjustKey"
      v-if="stockAdjustVisible"
      :visible="stockAdjustVisible"
      :product-id="currentProductId"
      :wholesaler-id="wholesalerId"
      :product-name="currentProductName"
      :unit="currentProductUnit"
      :current-stock="currentProductStock"
      :has-multi-warehouse="currentProductHasMultiWarehouse"
      @update:visible="stockAdjustVisible = $event"
      @saved="onStockAdjustSaved"
    />

    <!-- 批量调整库存弹窗 -->
    <div v-if="batchStockAdjustVisible" class="modal-overlay" @click="closeBatchStockAdjust">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <div class="modal-title">{{ $t('wholesaler.product.ProductList.line167_text') }}</div>
          <button class="modal-close" @click="closeBatchStockAdjust">×</button>
        </div>
        <div class="modal-body">
          <div class="batch-stock-info">
            <p>{{ $t('wholesaler.product.ProductList.line172_text') }} <strong>{{ selectedProducts.length }}</strong> {{ $t('wholesaler.product.ProductList.line172_text') }}</p>
            <p class="tip">{{ $t('wholesaler.product.ProductList.line173_text') }}</p>
          </div>
          
          <div class="form-group">
            <label>{{ $t('wholesaler.product.ProductList.line177_text') }}</label>
            <input 
              type="number" 
              v-model.number="batchAdjustQuantity"
              :placeholder="$t('wholesaler.product.ProductList.line181_placeholder')"
              step="0.01"
              class="form-input-large"
            />
          </div>
          
          <div class="form-group">
            <label>{{ $t('wholesaler.product.ProductList.line188_text') }}</label>
            <select v-model="batchAdjustReason" class="form-input-large">
              <option value="">{{ $t('wholesaler.product.ProductList.line190_text') }}</option>
              <option value="进货，走进货单流程" disabled>进货，走进货单流程</option>
              <option value="订单出库，走订单流程" disabled>订单出库，走订单流程</option>
              <option value="盘点调整">{{ $t('wholesaler.product.ProductList.line193_text') }}</option>
              <option value="报损">{{ $t('wholesaler.product.ProductList.line194_text') }}</option>
              <option value="退货">{{ $t('wholesaler.product.ProductList.line195_text') }}</option>
              <option value="调拨">{{ $t('wholesaler.product.ProductList.line196_text') }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>{{ $t('wholesaler.product.ProductList.line201_text') }}</label>
            <input 
              v-model="batchAdjustReasonText"
              :placeholder="$t('wholesaler.product.ProductList.line204_placeholder')"
              class="form-input-large"
            />
          </div>
          
          <div class="form-group">
            <label>{{ $t('wholesaler.product.ProductList.line210_text') }}</label>
            <input 
              v-model="batchAdjustNote"
              :placeholder="$t('wholesaler.product.ProductList.line213_placeholder')"
              class="form-input-large"
            />
          </div>
          
          <div class="warehouse-select-group">
            <label>{{ $t('wholesaler.product.ProductList.line219_text') }}</label>
            <select v-model="batchWarehouseId" class="form-input-large">
              <option value="">{{ $t('wholesaler.product.ProductList.line221_text') }}</option>
              <option v-for="w in warehouses" :key="w.id" :value="w.id">
                {{ w.warehouseName }} {{ w.isDefault ? $t('wholesaler.ProductList.default') : '' }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn modal-btn-cancel" @click="closeBatchStockAdjust">{{ $t('common.cancel') }}</button>
          <button class="modal-btn modal-btn-confirm" @click="confirmBatchStockAdjust" :disabled="!canBatchAdjust">
            {{ $t('wholesaler.ProductList.confirmAdjust') }}
          </button>
        </div>
      </div>
    </div>

    <!-- {{ $t('wholesaler.ProductList.batchPriceAdjust') }}弹窗 -->
    <div v-if="priceModalVisible" class="modal-overlay" @click="closePriceModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <div class="modal-title">{{ $t('wholesaler.product.ProductList.line241_text') }}</div>
          <button class="modal-close" @click="closePriceModal">×</button>
        </div>
        <div class="modal-body">
          <div class="price-adjust-tabs">
            <div class="adjust-tab" :class="{ active: adjustType === 'add' }" @click="adjustType = 'add'">{{ $t('wholesaler.product.ProductList.line246_text') }}</div>
            <div class="adjust-tab" :class="{ active: adjustType === 'reduce' }" @click="adjustType = 'reduce'">{{ $t('wholesaler.product.ProductList.line247_text') }}</div>
            <div class="adjust-tab" :class="{ active: adjustType === 'discount' }" @click="adjustType = 'discount'">{{ $t('wholesaler.product.ProductList.line248_text') }}</div>
          </div>
          
          <div v-if="adjustType === 'add'" class="adjust-input-group">
            <label>{{ $t('wholesaler.product.ProductList.line252_text') }}</label>
            <input type="number" v-model="adjustValue" :placeholder="$t('wholesaler.product.ProductList.line253_placeholder')" step="0.5">
          </div>
          
          <div v-if="adjustType === 'reduce'" class="adjust-input-group">
            <label>{{ $t('wholesaler.product.ProductList.line257_text') }}</label>
            <input type="number" v-model="adjustValue" :placeholder="$t('wholesaler.product.ProductList.line258_placeholder')" step="0.5">
          </div>
          
          <div v-if="adjustType === 'discount'" class="adjust-input-group">
            <label>{{ $t('wholesaler.product.ProductList.line262_text') }}</label>
            <input type="number" v-model="adjustValue" :placeholder="$t('wholesaler.product.ProductList.line263_placeholder')" step="0.5" min="1" max="10">
            <div style="font-size: 12px; color: #999; margin-top: 8px;">{{ $t('wholesaler.product.ProductList.line264_text') }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn modal-btn-cancel" @click="closePriceModal">{{ $t('common.cancel') }}</button>
          <button class="modal-btn modal-btn-confirm" @click="applyPriceAdjust">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 单个商品市场价编辑弹窗 -->
    <div v-if="marketPriceModalVisible" class="modal-overlay" @click="closeMarketPriceModal">
      <div class="modal market-price-modal" @click.stop>
        <div class="modal-header">
          <div class="modal-title">{{ $t('wholesaler.product.ProductList.line278_text') }}</div>
          <button class="modal-close" @click="closeMarketPriceModal">×</button>
        </div>
        <div class="modal-body">
          <div class="market-price-info">
            <div class="product-name">{{ editingProduct && editingProduct.name }}</div>
            <div class="current-price">{{ $t('wholesaler.ProductList.currentMarketPrice') }}C$ {{ (editingProduct && editingProduct.marketPrice) || '0.00' }}</div>
          </div>
          <div class="price-input-group">
            <label>{{ $t('wholesaler.product.ProductList.line287_text') }}</label>
            <input type="number" v-model="newMarketPrice" :placeholder="$t('wholesaler.product.ProductList.line288_placeholder')" step="0.0001" min="0">
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-btn modal-btn-cancel" @click="closeMarketPriceModal">{{ $t('common.cancel') }}</button>
          <button class="modal-btn modal-btn-confirm" @click="saveMarketPrice" :disabled="!newMarketPrice">{{ $t('common.save') }}</button>
        </div>
      </div>
    </div>

    <!-- 分享弹窗 -->
    <!-- 分享弹窗 -->
    <WholesalerShareDialog
      v-if="shareDialogVisible"
      :type="shareType"
      :product-ids="shareProductIds"
      @close="shareDialogVisible = false"
      @created="onShareCreated"
    />

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item active">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.product.ProductList.line320_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>

    <!-- 图片预览 -->
    <ImagePreview ref="imagePreview" />

  </div>
</template>

<script>
import api from '@/services/api';
import MultiWarehouseDialog from '@/components/MultiWarehouseDialog.vue';
import ImagePreview from '@/components/ImagePreview.vue';
import WholesalerShareDialog from '@/components/WholesalerShareDialog.vue';
import { getWholesalerId } from '@/utils/wholesaler';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'WholesalerProductList',
  components: {
    MultiWarehouseDialog,
    ImagePreview,
    WholesalerShareDialog
  },
  data() {
    return {
      stats: {
        total: 0,
        hasWholesalePrice: 0,
        onShelf: 0,
        customers: 0
      },
      products: [],
      filteredProducts: [],
      categories: [],
      currentCategory: '',
      searchQuery: '',
      loading: false,
      loadingMore: false,
      // 分页相关
      page: 0,
      pageSize: 20,
      totalPages: 0,
      hasMore: true,
      selectAll: false,
      selectedProducts: [],
      priceModalVisible: false,
      adjustType: 'add',
      adjustValue: null,
      wholesalerId: null,
      // 市场价编辑
      marketPriceModalVisible: false,
      editingProduct: null,
      newMarketPrice: null,
      // 库存调整
      stockAdjustVisible: false,
      stockAdjustKey: 0,
      currentProductId: null,
      currentProductName: '',
      currentProductUnit: this.$t('wholesaler.ProductList.kilogram'),
      currentProductStock: 0,
      currentProductHasMultiWarehouse: false,
      // 批量调整库存
      batchStockAdjustVisible: false,
      batchAdjustQuantity: null,
      batchAdjustReason: '',
      batchAdjustReasonText: '',
      batchAdjustNote: '',
      batchWarehouseId: null,
      warehouses: [],
      // 分享相关
      shareDialogVisible: false,
      shareType: 'product',
      shareProductIds: []
    }
  },
  computed: {
    canBatchAdjust() {
      return this.batchAdjustQuantity !== null && 
             this.batchAdjustQuantity !== 0 && 
             this.batchAdjustReason &&
             this.batchWarehouseId;
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 每次进入页面都刷新数据
      vm.loadProducts()
    })
  },
  beforeRouteUpdate(to, from, next) {
    // 路由参数变化时也刷新
    this.loadProducts()
    next()
  },
  mounted() {
    // 监听滚动事件
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getCategoryKey(categoryName) {
      // 将中文分类名转换为翻译键
      const map = {
        '水果类': 'fruit',
        '蔬菜类': 'vegetable',
        '肉类': 'meat',
        '水产': 'seafood',
        '粮油类': 'grain',
        '塑料制品': 'plastic',
        '乳品饮料': 'dairy',
        '冷冻食品': 'frozen'
      };
      return map[categoryName] || 'other';
    },
    getImageUrl,
    handleScroll() {
      // 检查是否滚动到底部
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = document.documentElement.clientHeight
      
      if (scrollHeight - scrollTop - clientHeight < 100 && !this.loadingMore && this.hasMore) {
        this.loadMore()
      }
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return
      if (!this.wholesalerId) return  // wholesalerId 还没获取到，跳过
      
      this.loadingMore = true
      try {
        const params = {
          page: this.page + 1,
          size: this.pageSize
        }
        if (this.searchQuery && this.searchQuery.trim()) {
          params.keyword = this.searchQuery.trim()
        }
        
        const result = await api.get(`/product/wholesaler/list/${this.wholesalerId}`, { params })
        
        if (result && result.content) {
          const newProducts = this.transformProducts(result.content)
          this.products = [...this.products, ...newProducts]
          this.filteredProducts = this.products
          this.page = result.page
          this.totalPages = result.totalPages
          this.hasMore = !result.last
        }
      } catch (error) {
        console.error('加载更多商品失败:', error)
      } finally {
        this.loadingMore = false
      }
    },
    transformProducts(list) {
      return list.map(p => {
        const totalStock = p.totalStock !== undefined ? p.totalStock : (p.shopStockQuantity || 0);
        return {
          ...p,
          marketPrice: p.marketPrice !== undefined ? p.marketPrice : (p.price || p.basePrice),
          costPrice: p.costPrice || 0,
          minWholesalePrice: p.minWholesalePrice || null,
          stock: totalStock,
          totalStock: totalStock,
          hasMultiWarehouse: p.hasMultiWarehouse || false,
          unit: p.unit || this.$t('wholesaler.ProductList.kilogram'),
          categoryName: p.categoryName || this.$t('wholesaler.ProductList.uncategorized'),
          sku: p.sku || 'N/A',
          hasWholesalePrice: !!p.minWholesalePrice,
          publishedCustomers: p.publishedCustomers || 0,
          mainImage: this.normalizeImageUrl(p.mainImage),
          images: this.normalizeImageUrls(p.images)
        };
      });
    },
    
    // 规范化单个图片 URL
    normalizeImageUrl(url) {
      if (!url) return url;
      if (url.startsWith('http')) {
        try {
          const urlObj = new URL(url);
          return urlObj.pathname; // 返回 /api/uploads/... 格式
        } catch (e) {
          return url;
        }
      }
      return url;
    },
    
    // 规范化图片 URL 数组
    normalizeImageUrls(images) {
      if (!images) return images;
      let imgList = images;
      if (typeof imgList === 'string') {
        try { imgList = JSON.parse(imgList); } catch(e) { return images; }
      }
      if (Array.isArray(imgList)) {
        return imgList.map(img => {
          const url = typeof img === 'string' ? img : img.url;
          return this.normalizeImageUrl(url);
        });
      }
      return images;
    },

    async getWholesalerId() {
      try {
        const response = await api.get('/auth/me');
        if (!response.wholesalerId) {
          throw new Error(this.$t('wholesaler.ProductList.wholesalerNotLinked'));
        }
        return response.wholesalerId;
      } catch (error) {
        console.error('获取批发商ID失败:', error);
        throw error;
      }
    },

    // 分享单个商品
    shareProduct(product) {
      this.shareType = 'product'
      this.shareProductIds = [product.id]
      this.shareDialogVisible = true
    },

    // this.$t('wholesaler.ProductList.batchShare')商品
    batchShareProducts() {
      if (this.selectedProducts.length === 0) {
        this.$message.warning(this.$t('wholesaler.ProductList.pleaseSelectProducts'))
        return
      }
      this.shareType = 'product'
      this.shareProductIds = [...this.selectedProducts]
      this.shareDialogVisible = true
    },

    // 分享创建成功回调 - 不关闭弹窗，让用户看到链接
    onShareCreated(shareData) {
      this.$message.success(this.$t('wholesaler.ProductList.shareLinkCreated'))
      console.log('分享创建成功:', shareData)
    },

    async loadProducts() {
      this.loading = true;
      this.page = 0;
      this.products = [];
      this.hasMore = true;
      try {
        this.wholesalerId = await this.getWholesalerId();
        console.log('加载商品列表，批发商 ID:', this.wholesalerId);
        
        const params = {
          page: 0,
          size: this.pageSize
        };
        if (this.searchQuery && this.searchQuery.trim()) {
          params.keyword = this.searchQuery.trim();
        }
        const result = await api.get(`/product/wholesaler/list/${this.wholesalerId}`, { params });
        console.log('📦 商品列表响应:', result);
        
        // 支持分页格式和旧数组格式
        if (result && result.content) {
          // 分页格式
          this.products = this.transformProducts(result.content);
          this.page = result.page;
          this.totalPages = result.totalPages;
          this.hasMore = !result.last;
        } else if (Array.isArray(result)) {
          // 旧数组格式（兼容）
          this.products = this.transformProducts(result);
          this.hasMore = false;
        }
        
        this.filteredProducts = this.products;
        
        // 提取分类列表
        const categoryMap = {};
        this.products.forEach(p => {
          if (!categoryMap[p.categoryName]) {
            categoryMap[p.categoryName] = { name: p.categoryName, icon: this.getProductIcon(p), count: 0 };
          }
          categoryMap[p.categoryName].count++;
        });
        this.categories = Object.values(categoryMap);
        
        // 更新统计
        this.stats.total = result.totalElements || this.products.length;
        this.stats.hasWholesalePrice = this.products.filter(p => p.minWholesalePrice).length;
        this.stats.onShelf = this.products.filter(p => p.enabled).length;
        this.stats.customers = 25;
        
        console.log('商品列表加载成功，共', this.products.length, this.$t('wholesaler.ProductList.items'));
        if (this.products.length > 0) {
          console.log('   示例:', this.products[0]?.name, '最低价:', this.products[0]?.minWholesalePrice);
        }
      } catch (error) {
        console.error('加载商品失败:', error);
        const message = error.response?.data?.message || error.message || this.$t('wholesaler.ProductList.loadFailed');
        this.showToast(message, 'error');
      } finally {
        this.loading = false;
      }
    },

    getProductIcon(product) {
      const icons = { 
        '水果类': '🍎', 
        '蔬菜类': '🥬', 
        '肉类': '🥩', 
        '水产': '🐟', 
        '粮油': '🌾',
        '未分类': '📦'
      };
      return icons[product.categoryName] || '📦';
    },

    handleSelectAll() {
      if (this.selectAll) {
        this.selectedProducts = this.products.map(p => p.id);
      } else {
        this.selectedProducts = [];
      }
    },

    async batchOnshelf() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      
      try {
        // 调用后端 API：PUT /api/product/{id}/status，传 JSON 对象
        const promises = this.selectedProducts.map(id => 
          api.put(`/product/${id}/status`, { enabled: true }, { headers: { 'Content-Type': 'application/json' } })
        );
        await Promise.all(promises);
        
        this.showToast(this.$t('wholesaler.ProductList.listedCount', { count: this.selectedProducts.length }), 'success');
        await this.loadProducts();
        this.selectedProducts = [];
        this.selectAll = false;
      } catch (error) {
        console.error('Batch listing failed:', error);
        const message = error.response?.data?.message || this.$t('wholesaler.ProductList.batchListingFailed');
        this.showToast(message, 'error');
      }
    },

    async batchOffshelf() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      
      try {
        // 调用后端 API：PUT /api/product/{id}/status，传 JSON 对象
        const promises = this.selectedProducts.map(id => 
          api.put(`/product/${id}/status`, { enabled: false }, { headers: { 'Content-Type': 'application/json' } })
        );
        await Promise.all(promises);
        
        this.showToast(this.$t('wholesaler.ProductList.unlistedCount', { count: this.selectedProducts.length }), 'success');
        await this.loadProducts();
        this.selectedProducts = [];
        this.selectAll = false;
      } catch (error) {
        console.error(this.$t('wholesaler.ProductList.batchDelisting') + '失败:', error);
        const message = error.response?.data?.message || this.$t('wholesaler.ProductList.batchDelistingFailed');
        this.showToast(message, 'error');
      }
    },

    openPriceModal() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      this.priceModalVisible = true;
      this.adjustType = 'add';
      this.adjustValue = null;
    },

    closePriceModal() {
      this.priceModalVisible = false;
    },

    async applyPriceAdjust() {
      if (!this.adjustValue || this.adjustValue <= 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseEnterValidNumber'), 'error');
        return;
      }
      
      try {
        // 计算新价格
        const adjustments = this.selectedProducts.map(productId => {
          const product = this.products.find(p => p.id === productId);
          let newPrice = product.marketPrice || product.price || product.basePrice || 0;
          newPrice = parseFloat(newPrice);
          if (isNaN(newPrice)) newPrice = 0;
          const adjustValue = parseFloat(this.adjustValue) || 0;
          
          if (this.adjustType === 'add') {
            newPrice += adjustValue;
          } else if (this.adjustType === 'reduce') {
            newPrice -= adjustValue;
          } else if (this.adjustType === 'discount') {
            newPrice = newPrice * (adjustValue / 10);
          }
          
          return {
            id: productId,
            price: parseFloat(newPrice.toFixed(4))
          };
        });
        
        console.log(this.$t('wholesaler.ProductList.batchPriceAdjust') + ':', adjustments);
        
        // 调用后端 API：PUT /api/product/{id}/price
        const promises = adjustments.map(adj => 
          api.put(`/product/${adj.id}/price`, { 
            price: adj.price
          })
        );
        await Promise.all(promises);
        
        this.showToast(this.$t('wholesaler.ProductList.adjustedCount', {count: this.selectedProducts.length}), 'success');
        this.closePriceModal();
        await this.loadProducts();
        this.selectedProducts = [];
        this.selectAll = false;
      } catch (error) {
        console.error(this.$t('wholesaler.ProductList.batchPriceAdjust') + '失败:', error);
        const message = error.response?.data?.message || error.message || this.$t('wholesaler.ProductList.batchPriceUpdateFailed');
        this.showToast(message, 'error');
      }
    },

    goToWholesalePrice() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      // 跳转到创建批发报价页面，带入已选商品 ID
      this.$router.push({
        path: '/wholesaler/create-package',
        query: {
          productIds: this.selectedProducts.join(',')
        }
      });
    },

    // 跳转到this.$t('wholesaler.ProductList.wholesalePrice')编辑页
    goToWholesalePriceList(product) {
      if (product.minWholesalePricePackageId) {
        // 有this.$t('wholesaler.ProductList.wholesalePrice') → 跳转编辑对应批发报价
        this.$router.push({
          path: '/wholesaler/create-package',
          query: { packageId: product.minWholesalePricePackageId }
        });
      } else {
        // 没有this.$t('wholesaler.ProductList.wholesalePrice') → 跳转创建批发报价，预选中该商品
        this.$router.push({
          path: '/wholesaler/create-package',
          query: { productIds: String(product.id) }
        });
      }
    },

    manageWholesalePrice(product) {
      this.goToWholesalePriceList(product);
    },

    editProduct(product) {
      // 跳转到商品详情页
      console.log('编辑商品，商品 ID:', product.id);
      this.$router.push(`/wholesaler/product/${product.id}`);
    },

    addProduct() {
      // 跳转到商品编辑页面（新增模式）
      this.$router.push('/wholesaler/product/create');
    },

    goBack() {
      this.$router.back();
    },

    // 分类筛选
    filterByCategory(categoryName) {
      this.currentCategory = categoryName;
      this.applyFilters();
    },

    // 搜索
    async handleSearch() {
      await this.loadProducts();
    },

    handleImageError(e) {
      e.target.style.display = 'none';
    },

    // 应用筛选
    applyFilters() {
      let filtered = this.products;
      
      // 分类筛选
      if (this.currentCategory) {
        filtered = filtered.filter(p => p.categoryName === this.currentCategory);
      }
      
      // 搜索筛选
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(query) || 
          (p.sku && p.sku.toLowerCase().includes(query))
        );
      }
      
      this.filteredProducts = filtered;
    },

    // 切换商品上下架状态
    async toggleProductStatus(product) {
      try {
        const enabled = !product.enabled;
        
        await api.put(`/product/${product.id}/status`, { enabled });
        
        this.showToast(enabled ? this.$t('wholesaler.ProductList.onShelf') : this.$t('wholesaler.ProductList.offShelf'), 'success');
        await this.loadProducts();
      } catch (error) {
        console.error('切换状态失败:', error);
        this.showToast(this.$t('wholesaler.ProductList.operationFailed') + '：' + (error.response?.data?.message || error.message), 'error');
      }
    },

    // 复制商品
    async copyProduct(product) {
      try {
        const copyData = {
          ...product,
          name: product.name + this.$t('wholesaler.ProductList.copySuffix'),
          sku: 'SKU-' + Date.now(),
          // 确保价格字段正确传递
          marketPrice: product.marketPrice || product.price || 0,
          costPrice: product.costPrice || 0,
          price: product.marketPrice || product.price || 0
        };
        
        // 移除不需要的字段
        delete copyData.id;
        delete copyData.createdAt;
        delete copyData.updatedAt;
        
        console.log('📋 复制商品数据:', copyData);
        
        await api.post('/product', copyData);
        this.showToast(this.$t('wholesaler.ProductList.productCopied'), 'success');
        await this.loadProducts();
      } catch (error) {
        console.error('复制商品失败:', error);
        this.showToast(this.$t('wholesaler.ProductList.copyFailed') + (error.response?.data?.message || error.message), 'error');
      }
    },

    openBatchSettings() {
      alert(this.$t('wholesaler.ProductList.featureInDevelopment'));
    },

    showToast(message, type = 'info') {
      const toast = document.createElement('div');
      const bgColor = type === 'error' 
        ? '#ff4d4f' 
        : type === 'success' 
          ? '#52c41a' 
          : 'rgba(0,0,0,0.85)';
      
      toast.className = 'custom-toast';
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: ${bgColor};
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        z-index: 9999;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        white-space: nowrap;
      `;
      toast.textContent = message;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
    },

    // ========== 代客下单跳转方法 ==========
    
    // 单个商品代客下单 - 跳转到代客下单页面
    goToSingleOrder(product) {
      this.$router.push({
        path: '/wholesaler/order-on-behalf',
        query: {
          productIds: product.id,
          mode: 'single'
        }
      });
    },

    // 批量代客下单 - 跳转到代客下单页面
    goToBatchOrder() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      this.$router.push({
        path: '/wholesaler/order-on-behalf',
        query: {
          productIds: this.selectedProducts.join(','),
          mode: 'batch'
        }
      });
    },

    // 编辑市场价
    editMarketPrice(product) {
      console.log('🔍 点击市场价编辑，商品:', product.name);
      this.editingProduct = product;
      this.newMarketPrice = product.marketPrice || 0;
      this.marketPriceModalVisible = true;
      this.$nextTick(() => {
        console.log('✅ modalVisible after nextTick:', this.marketPriceModalVisible);
      });
    },

    closeMarketPriceModal() {
      this.marketPriceModalVisible = false;
      this.editingProduct = null;
      this.newMarketPrice = null;
    },

    async saveMarketPrice() {
      if (!this.editingProduct || !this.newMarketPrice) return;
      try {
        const productId = this.editingProduct.id;
        const newPrice = parseFloat(this.newMarketPrice);
        await api.put(`/product/${productId}/price`, { price: newPrice });
        // 更新本地数据
        this.editingProduct.marketPrice = newPrice;
        this.showToast(this.$t('wholesaler.ProductList.marketPriceUpdated'), 'success');
        this.closeMarketPriceModal();
      } catch (error) {
        console.error('保存市场价失败:', error);
        this.showToast(this.$t('wholesaler.ProductList.saveFailed') + (error.response?.data?.message || error.message), 'error');
      }
    },

    // 打开库存调整弹窗
    openStockAdjust(product) {
      this.currentProductId = product.id;
      this.currentProductName = product.name;
      this.currentProductUnit = product.unit || this.$t('wholesaler.ProductList.kilogram');
      this.currentProductStock = product.totalStock || 0;
      this.currentProductHasMultiWarehouse = product.hasMultiWarehouse || false;
      // ⭐ 强制重新创建组件，确保加载最新代码
      this.stockAdjustKey++;
      // 根据单仓库/多仓库模式，弹窗会自动判断显示什么界面
      this.stockAdjustVisible = true;
      console.log('📦 打开库存调整弹窗:', {
        productId: product.id,
        productName: product.name,
        unit: product.unit,
        hasMultiWarehouse: product.hasMultiWarehouse,
        totalStock: product.totalStock,
        stock: product.totalStock || 0
      });
    },

    // 库存调整保存后
    async onStockAdjustSaved() {
      this.stockAdjustVisible = false;
      // 重新加载商品列表，更新库存
      await this.loadProducts();
    },

    // 打开批量调整库存弹窗
    async openBatchStockAdjust() {
      if (this.selectedProducts.length === 0) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseSelectProducts'), 'error');
        return;
      }
      
      // 加载仓库列表
      try {
        const res = await api.get(`/warehouse/list?wholesalerId=${this.wholesalerId}`);
        this.warehouses = Array.isArray(res) ? res : (res.data || []);
        if (this.warehouses.length > 0) {
          // 默认选择默认仓库
          const defaultWarehouse = this.warehouses.find(w => w.isDefault);
          this.batchWarehouseId = defaultWarehouse ? defaultWarehouse.id : this.warehouses[0].id;
        }
        this.batchStockAdjustVisible = true;
      } catch (error) {
        console.error('加载仓库列表失败:', error);
        this.showToast(this.$t('wholesaler.ProductList.loadWarehouseListFailed'), 'error');
      }
    },

    // 关闭批量调整库存弹窗
    closeBatchStockAdjust() {
      this.batchStockAdjustVisible = false;
      this.batchAdjustQuantity = null;
      this.batchAdjustReason = '';
      this.batchAdjustNote = '';
      this.batchWarehouseId = null;
    },

    // 确认批量调整库存
    async confirmBatchStockAdjust() {
      if (!this.canBatchAdjust) {
        this.showToast(this.$t('wholesaler.ProductList.pleaseFillAdjustInfo'), 'error');
        return;
      }

      try {
        // 为每个选中的商品创建调整请求
        const promises = this.selectedProducts.map(productId => {
          const payload = [{
            warehouseId: this.batchWarehouseId,
            adjustQuantity: this.batchAdjustQuantity,
            adjustReason: this.batchAdjustReason || this.$t('wholesaler.ProductList.manualAdjust'),
            reason: this.batchAdjustReasonText,
            note: this.batchAdjustNote
          }];
          return api.post(`/warehouse/product/${productId}/stocks/adjust-batch`, payload);
        });

        await Promise.all(promises);

        this.showToast(this.$t('wholesaler.ProductList.adjustedStockCount', {count: this.selectedProducts.length}), 'success');
        this.closeBatchStockAdjust();
        await this.loadProducts();
        this.selectedProducts = [];
        this.selectAll = false;
      } catch (error) {
        console.error('批量调整库存失败:', error);
        const message = error.response?.data?.message || this.$t('wholesaler.ProductList.batchAdjustFailed');
        this.showToast(message, 'error');
      }
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      };
      if (routes[tab]) {
        this.$router.push(routes[tab]);
      }
    },
    /**
     * 获取商品的图片列表
     */
    getProductImages(product) {
      const images = [];
      if (product.mainImage && product.mainImage.startsWith('http')) {
        images.push(product.mainImage);
      }
      // 后端可能返回 JSON 字符串或数组
      if (product.images) {
        let imgList = product.images;
        if (typeof imgList === 'string') {
          try { imgList = JSON.parse(imgList); } catch(e) { imgList = []; }
        }
        if (Array.isArray(imgList)) {
          imgList.forEach(img => {
            const url = typeof img === 'string' ? img : img.url;
            if (url && url.startsWith('http') && !images.includes(url)) {
              images.push(url);
            }
          });
        }
      }
      return images;
    },
    /**
     * 打开图片预览
     */
    openImagePreview(product) {
      const images = this.getProductImages(product);
      if (images.length > 0) {
        this.$refs.imagePreview.open(images, 0);
      }
    }
  }
}
</script>

<style scoped>
.product-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 60px;
}

.back-header {
  background: linear-gradient(135deg, #ffc069 0%, #fa8c16 100%);
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 2px 8px rgba(250, 140, 22, 0.15);
}

.back-btn {
  position: absolute;
  left: 16px;
  font-size: 20px;
  color: white;
  cursor: pointer;
}

.back-header h1 {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.header-actions {
  position: absolute;
  right: 16px;
}

.add-btn {
  padding: 8px 16px;
  border: none;
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 12px;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #667eea;
}

.stat-label {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.batch-action-bar {
  background: white;
  border-radius: 10px;
  padding: 12px;
  margin: 0 12px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.select-all {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
}

.select-all input {
  width: 16px;
  height: 16px;
}

.batch-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.batch-btn {
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.btn-onshelf {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.btn-offshelf {
  background: #f0f0f0;
  color: #666;
}

.btn-price {
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
}

.btn-batch {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-order {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.product-list {
  padding: 12px;
}

.product-item {
  background: white;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  border-left: 4px solid transparent;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

.product-item.onshelf {
  border-left-color: #52c41a;
}

.product-item.offshelf {
  border-left-color: #999;
}

/* 商品名称标题样式 - 像作文标题 */
.product-name-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  padding: 0 0 8px 4px; /* 与 border-left 对齐 */
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.4;
  text-align: left;
  width: 100%; /* 确保占满宽度 */
  box-sizing: border-box; /* 包含 padding 在宽度内 */
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 状态圆点 */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.status-dot.onshelf {
  background: #52c41a;
  box-shadow: 0 0 4px rgba(82, 196, 26, 0.5);
}

.status-dot.offshelf {
  background: #999;
}

/* 状态标签 */
.status-label {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: auto;
}

.status-label.onshelf {
  background: #f6ffed;
  color: #52c41a;
}

.status-label.offshelf {
  background: #f5f5f5;
  color: #999;
}

/* 商品主体 - 左(checkbox+图片) 右(信息) */
.product-body {
  display: flex;
  gap: 12px;
  margin-left: 0;
}

.product-left {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 0;
}

.product-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-top: 20px;
  flex-shrink: 0;
  margin-left: 0;
}

.product-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.product-image {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.product-meta {
  font-size: 11px;
  color: #999;
  margin-bottom: 8px;
}

.price-display {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
  padding: 10px;
  background: #fafafa;
  border-radius: 8px;
}

.price-item {
  flex: 1;
  text-align: center;
}

.price-label {
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.price-value {
  font-size: 16px;
  font-weight: 700;
}

.market-price-value {
  color: #666;
}

.wholesale-price-value {
  color: #ff4d4f;
}

.wholesale-info-bar {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.wholesale-info-bar .icon {
  font-size: 14px;
}

.wholesale-info-bar .title {
  color: #52c41a;
  font-weight: 600;
}

.wholesale-info-bar .count {
  margin-left: auto;
  color: #999;
}

/* 最低{{ $t('wholesaler.ProductList.wholesalePrice') }}提示 */
.price-tip {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  text-align: center;
}

.info-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.info-tag {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 12px;
}

.stock-tag {
  background: #e6f4ff;
  color: #1890ff;
}

.warehouse-tag {
  background: #f0f5ff;
  color: #597ef7;
}

.status-tag {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
}

.status-onshelf {
  background: #f6ffed;
  color: #52c41a;
}

.status-offshelf {
  background: #f5f5f5;
  color: #999;
}

.product-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
}

.btn-success {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.batch-btn.stock {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.batch-btn.order {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

/* 价格编辑图标 */
.price-value-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  position: relative;
  pointer-events: auto;
}

.price-value-wrapper:hover .edit-icon {
  opacity: 1;
}

.edit-icon {
  font-size: 9px;
  opacity: 0.5;
  transition: opacity 0.2s;
  cursor: pointer;
}

/* 库存编辑样式 */
.stock-value-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  position: relative;
}

.stock-value-wrapper:hover .edit-icon {
  opacity: 1;
}

/* 库存列样式 - 左右结构 */
.stock-column {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stock-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stock-label {
  font-size: 11px;
  color: #999;
}

.stock-value-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  position: relative;
}

.stock-value-wrapper:hover .edit-icon {
  opacity: 1;
}

.stock-value {
  font-size: 13px;
  font-weight: 600;
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stock-value.multi-warehouse {
  color: #fa8c16;
}

.multi-tag {
  background: linear-gradient(135deg, #ffc069, #fa8c16);
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
}

/* 批量调整库存信息 */
.batch-stock-info {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.batch-stock-info p {
  margin: 4px 0;
  font-size: 14px;
  color: #666;
}

.batch-stock-info .tip {
  font-size: 12px;
  color: #fa8c16;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-input-large {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-input-large:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.warehouse-select-group {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 1 !important;
  visibility: visible !important;
  animation: none !important;
}

.modal {
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.modal-body {
  margin-bottom: 20px;
}

.price-adjust-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.adjust-tab {
  flex: 1;
  padding: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  cursor: pointer;
}

.adjust-tab.active {
  border-color: #fa8c16;
  background: #fff7e6;
  color: #fa8c16;
}

.adjust-input-group {
  margin-bottom: 12px;
}

.adjust-input-group label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.adjust-input-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 15px;
  text-align: center;
}

.modal-footer {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.modal-btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.modal-btn-confirm {
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
}

/* ========== 主布局 ========== */
.main-content {
  display: flex;
  gap: 12px;
  padding: 12px;
}

/* ========== 左侧分类导航 ========== */
.category-sidebar {
  width: 80px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 4px;
  padding: 6px;
  height: calc(100vh - 140px);
  overflow-y: auto;
}

.category-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
}

.category-item {
  padding: 4px 6px;
  margin-bottom: 2px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  color: #666;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-item:hover {
  background: #f5f5f5;
}

.category-item.active {
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
  font-weight: 600;
}

/* ========== 右侧商品列表 ========== */
.product-list-wrapper {
  flex: 1;
  min-width: 0;
}

.batch-action-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
}

.select-all {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}

.batch-actions {
  display: flex;
  gap: 8px;
}

.batch-btn {
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.batch-btn.onshelf {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.batch-btn.offshelf {
  background: linear-gradient(135deg, #bfbfbf, #8c8c8c);
  color: white;
}

.batch-btn.price {
  background: linear-gradient(135deg, #69c0ff, #1890ff);
  color: white;
}

.batch-btn.wholesale {
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  color: white;
}

.batch-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.batch-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  transform: none;
}

.search-bar {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}

.search-input:focus {
  outline: none;
  border-color: #fa8c16;
}

.product-list {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.product-image {
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
}

.product-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
  display: block;
}

.product-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 28px;
}

/* 多图指示器 */
.image-count-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 8px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}

.product-info {
  flex: 1;
  min-width: 0;
}

/* 商品名称标题样式 - 像作文标题 */
.product-name-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
  line-height: 1.4;
}

/* 底部按钮区域 */
.product-actions-bottom {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.edit-btn {
  padding: 4px 12px;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn:hover {
  border-color: #fa8c16;
  color: #fa8c16;
}

.edit-btn.order-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
}

.edit-btn.order-btn:hover {
  opacity: 0.9;
}

.product-status {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.product-status.onshelf {
  background: #f6ffed;
  color: #52c41a;
}

.product-status.onshelf:hover {
  background: #d9f7be;
}

.product-status.offshelf {
  background: #f5f5f5;
  color: #999;
}

.product-status.offshelf:hover {
  background: #e0e0e0;
}

.product-meta {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.price-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.price-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-label {
  font-size: 11px;
  color: #999;
}

.price-value {
  font-size: 13px;
  font-weight: 600;
  color: #fa8c16;
}

.price-value.wholesale {
  cursor: pointer;
}

.price-value.wholesale:hover {
  color: #d46b08;
}

.price-action {
  font-size: 11px;
  color: #fa8c16;
  cursor: pointer;
  font-weight: 600;
}

.price-action:hover {
  color: #d46b08;
}

.product-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: #f0f0f0;
  color: #666;
}

.action-btn.primary {
  background: #fa8c16;
  color: white;
}

.action-btn.arrow {
  padding: 4px 10px;
  font-size: 18px;
  background: transparent;
  color: #999;
}

.action-btn.arrow:hover {
  color: #fa8c16;
}

/* 市场价编辑弹窗 */
.market-price-modal {
  opacity: 1 !important;
  visibility: visible !important;
  animation: none !important;
}

.market-price-modal .market-price-info {
  margin-bottom: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
}

.market-price-modal .product-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.market-price-modal .current-price {
  font-size: 13px;
  color: #999;
}

.price-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-input-group label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.price-input-group input {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
}

.price-input-group input:focus {
  border-color: #667eea;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

/* 分享按钮样式 */
.share-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.share-btn:hover {
  background: linear-gradient(135deg, #5568d3 0%, #653a8c 100%);
}

.batch-btn.share {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.batch-btn.share:hover:not(:disabled) {
  background: linear-gradient(135deg, #5568d3 0%, #653a8c 100%);
}

.batch-btn.share:disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}

</style>
